import { useEffect, useState } from 'react';
import {
  Route,
  RouteComponentProps,
  Switch,
  withRouter,
} from 'react-router-dom';
import { connect } from 'react-redux';
import {
  useTranslation,
  withTranslation,
  WithTranslation,
} from 'react-i18next';

import * as api from '../../../../api';
import * as store from '../../../../reducers/store';
import * as UploadUtils from '../../../../scenes/Form/UploadDocumentPage/utils';

import { Alert } from '@vivaahealth/design-system';
import UploadCard from '../../UploadCardContainer/components/UploadCard/UploadCard';
import AddPhotoIdPage from './AddPhotoIdPage/AddPhotoIdPage';
import Loader from '../../../../components/Loader';
import ReviewUploadPage from '../../ReviewUploadPage/ReviewUploadPage';
import { getSelectedPracticePreference } from '../../../../utils/formSet';

interface StateProps {
  practicePreference: api.Previsit.PracticePreference;
}

interface PhotoIdRouterProps
  extends StateProps,
    RouteComponentProps,
    WithTranslation {
  field: api.PrevisitFormField.PrevisitFormFieldWithConfig;
  encounter: api.Encounter.Encounter;
  setFormField: (formFieldId: string, value: string) => void;
  navigateToNextPage: () => Promise<void>;
}

const PhotoIdRouter = ({
  field,
  encounter,
  practicePreference,
  setFormField,
  navigateToNextPage,
  match,
  history,
  location,
}: PhotoIdRouterProps) => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [photoId, setPhotoId] =
    useState<api.PatientUpload.PatientUpload | null>(null);

  useEffect(() => {
    if (photoId) return;
    const getLatestUpload = async () => {
      try {
        const result = await UploadUtils.getLatestUpload({
          documentType: api.PatientUpload.DocumentType.PhotoID,
          encounterId: encounter.id,
          setUpload: setPhotoId,
          onComplete: () => {},
        });
        if (result) {
          setPhotoId(result);
          history.replace(`${match.url}/review` + location.search);
        }
      } catch (e) {
        setErrorMessage(t('errorOccurredTryAgain'));
      } finally {
        setIsLoading(false);
      }
    };
    getLatestUpload();
  }, [encounter.id, history, location.search, match.url, photoId, t]);

  if (isLoading) {
    return <Loader />;
  }
  if (errorMessage) {
    return <Alert variant="danger">{errorMessage}</Alert>;
  }

  return (
    <Switch>
      <Route exact path={`${match.path}/`}>
        <AddPhotoIdPage
          isRequired={field.required}
          field={field}
          practicePreference={practicePreference}
          setFormField={setFormField}
          navigateToNextPage={navigateToNextPage}
          onAddNewCard={async () => {
            history.push(`${match.url}/upload` + location.search);
          }}
        />
      </Route>
      <Route path={`${match.path}/upload`}>
        <UploadCard
          onContinue={async (pictures) => {
            const newFormFieldValue = JSON.stringify({
              didNotHaveCard: false,
            });
            setFormField(field.id, newFormFieldValue);
            await api.client.Previsit.updateFormField(
              field.previsitFormSetId,
              field.id,
              newFormFieldValue,
            );
            await UploadUtils.uploadDocument({
              pictures,
              documentType: api.PatientUpload.DocumentType.PhotoID,
              encounterId: encounter.id,
              errorMessage: t('errorOccurredTryAgain'),
              setDocument: setPhotoId,
              setErrorMessage,
              onComplete: () => {
                history.push(`${match.url}/review` + location.search);
              },
            });
          }}
        />
      </Route>
      <Route path={`${match.path}/review`}>
        {photoId && (
          <ReviewUploadPage
            upload={photoId}
            uploadErrorMessage={errorMessage}
            title={t('review', { ns: 'photoId' })}
            navigateToUploadPage={async () => {
              history.push(`${match.url}/upload` + location.search);
            }}
            navigateToNextPage={navigateToNextPage}
          />
        )}
      </Route>
    </Switch>
  );
};

const mapStateToProps = (state: store.FormSetSelected): StateProps => {
  const selectedPracticePreference = getSelectedPracticePreference(state);
  return {
    practicePreference: selectedPracticePreference,
  };
};

export default withRouter(
  connect(mapStateToProps)(withTranslation()(PhotoIdRouter)),
);
